// The layers added on top of the base style: the ocean mask, country labels, the
// optional basemap detail, the weather dots and their names.
//
// Split from map/style.ts on a real seam rather than by size. A style is data —
// baseStyle returns a StyleSpecification and touches nothing — whereas these run
// against a live map, fetch, and read app state. Keeping the pure half pure is
// what lets it be reasoned about; this half is the imperative remainder.

import { map } from './instance.ts';
import { state } from '../state.ts';
import { visibleAtZoom, dotFilter, labelFilter, byPop, isOpenCity, setHighlightId } from './filters.ts';
import { DETAIL_ZOOM, LAND_URL } from './style.ts';
import { mapTheme } from './theme.ts';
import { addWindLayer } from './wind-sprites.ts';
import { colorExpression, colorExpressionOn } from '../palettes.ts';
import { buildOceanMask } from '../../lib/ocean-mask.ts';
import type { LandCollection } from '../../lib/ocean-mask.ts';
import type { FilterSpecification, GeoJSONSource } from 'maplibre-gl';
import type { GeoJSON } from 'geojson';

export async function addOceanMask() {
  const res = await fetch(LAND_URL, { cache: 'force-cache' });
  if (!res.ok) throw new Error(`${LAND_URL} -> ${res.status}`);
  const fc = (await res.json()) as LandCollection;
  const { mask, islands } = buildOceanMask(fc);
  // ocean-mask.ts models positions as readonly tuples, which @types/geojson's
  // mutable number[] does not accept — structurally the same JSON either way.
  (map.getSource('land') as GeoJSONSource).setData(fc as unknown as GeoJSON);
  (map.getSource('ocean-mask') as GeoJSONSource).setData(mask as unknown as GeoJSON);
  (map.getSource('mask-islands') as GeoJSONSource).setData(islands as unknown as GeoJSON);
}



// Country names go in last, so they are the topmost symbol layer and win every
// collision. MapLibre places symbols top-down, and the country label is the one thing
// on this map a reader cannot work out for themselves: a city name is its own label,
// but nothing tells you which country you are looking at. Placed under the cities they
// lost almost every time — at zoom 5 across Europe exactly five countries got a name
// against 231 city labels, and the five were Denmark, Guernsey and Luxembourg, small
// enough to have empty space of their own.
// Countries that have no cities in the dataset at all, so their name would float
// over a region with no dots and no readings — the SimpleMaps coverage omits them
// (Russia, Belarus, Iran, Israel, Lebanon, North Korea, Palestine, Western Sahara)
// plus uninhabited/territorial entries. Their labels are hidden so a name never
// promises data the map doesn't have. Derived from cities.json vs the Natural Earth
// label names, matched by ISO code (name-variants like "United States of America"
// or "People's Republic of China" ARE in the data and stay). Regenerate by diffing
// cities.json iso2 against the label set if the city source changes.
const HIDDEN_COUNTRY_LABELS = [
  'Anguilla', 'Antarctica', 'Ashmore and Cartier Islands', 'Australian Indian Ocean Territories',
  'Belarus', 'British Indian Ocean Territory', 'French Southern and Antarctic Lands', 'Guernsey',
  'Heard Island and McDonald Islands', 'Iran', 'Israel', 'Lebanon', 'Nauru', 'Norfolk Island',
  'North Korea', 'Palestine', 'Pitcairn Islands', 'Russia', 'Saint Barthélemy',
  'Saint Pierre and Miquelon', 'Siachen Glacier', 'Sint Maarten', 'Somaliland',
  'Turkish Republic of Northern Cyprus', 'United States Virgin Islands', 'Vatican City',
  'Wallis and Futuna', 'Western Sahara', 'Åland',
];

export function addCountryLabels() {
  map.addLayer({
    id: 'country-labels',
    type: 'symbol',
    source: 'country-labels',
    // Same idiom as the city dots: every feature carries the zoom it earns its
    // label at, baked from Natural Earth's MIN_LABEL by build-basemap.ts. Countries
    // with no cities in the data are dropped so their name never floats over an
    // empty region.
    filter: ['all', visibleAtZoom, ['!', ['in', ['get', 'name'], ['literal', HIDDEN_COUNTRY_LABELS]]]],
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Regular'],
      // Uppercase and spaced out: reads as territory rather than as a place, so a
      // country name is never mistaken for one of the city labels beneath it.
      'text-transform': 'uppercase',
      'text-letter-spacing': 0.12,
      'text-size': ['interpolate', ['linear'], ['zoom'], 1, 9, 4, 12, 8, 15],
      'text-max-width': 7,
      'text-padding': 4,
      'symbol-sort-key': ['get', 'rank'], // Natural Earth's LABELRANK: lower is more important
    },
    paint: {
      // Dimmed: between the land tint and the coastline, well short of the cities'
      // near-white. Winning the collision is about being *there*, not being loud.
      'text-color': mapTheme().countryLabel,
      'text-halo-color': mapTheme().labelHalo,
      'text-halo-width': 1.2,
    },
  });
}

// Country borders: reference geography, above the mask (which would otherwise paint
// over them) and below the weather.
export function addBasemapDetailLayers() {
  // Two layers, not one with a data-driven dash: line-dasharray is a cross-faded
  // property that only accepts zoom, so the dash pattern cannot vary per feature.
  // Settled borders get a confident dash; anything Natural Earth flags as disputed,
  // indefinite or a line of control gets a finer, fainter one.
  //
  // A border has to hold its own against a saturated field, so it is lighter than
  // the coastline rather than dimmer than it. Telling the two apart is the dash's
  // job, not a difference in weight: a hairline at low opacity in the coastline's
  // own colour simply disappears.
  const boundaryLine = (id: string, filter: FilterSpecification, dash: number[], opacity: number) =>
    map.addLayer({
      id,
      type: 'line',
      source: 'boundaries',
      filter,
      paint: {
        'line-color': mapTheme().border,
        'line-dasharray': dash,
        'line-opacity': opacity,
        'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.5, 8, 1.6],
      },
    });
  boundaryLine('country-boundaries', ['==', ['get', 'cls'], 'int'], [3, 2], 0.55);
  boundaryLine('country-boundaries-disputed', ['!=', ['get', 'cls'], 'int'], [1, 2], 0.4);
}

export function addWeatherLayers() {
  const v = state.variable;
  // Arrows first: above the ocean mask and the coastline, below the city dots and
  // every label, so a dot always wins where the two land on each other.
  addWindLayer();
  // A dot only draws where the variable has a value AND the city is big enough
  // for this zoom — plus the open city, always (see dotFilter).
  const cityFilter = dotFilter;
  // Soft glow halo beneath the crisp points.
  map.addLayer({
    id: 'weather-glow',
    type: 'circle',
    source: 'weather',
    filter: cityFilter(v),
    paint: {
      'circle-color': colorExpression(v),
      'circle-blur': 1,
      'circle-opacity': 0.35,
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, byPop(5, 11), 8, byPop(12, 30)],
    },
  });
  map.addLayer({
    id: 'weather-points',
    type: 'circle',
    source: 'weather',
    filter: cityFilter(v),
    paint: {
      'circle-color': colorExpression(v),
      // Size carries population, so the eye reads the big cities first at any zoom.
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, byPop(1.8, 4.5), 8, byPop(4, 12)],
      'circle-stroke-color': mapTheme().dotStroke,
      'circle-stroke-width': 0.6,
      'circle-opacity': 0.95,
    },
  });
  // The open city's persistent green ring — the steady twin of the hover pulse, so
  // the selected dot stays marked while its briefing is up. Filtered by id (set by
  // setSelectedHighlight); a never-match until a city is opened. No glow.
  map.addLayer({
    id: 'city-selected-ring',
    type: 'circle',
    source: 'weather',
    filter: ['==', ['get', 'id'], -1],
    paint: {
      'circle-color': 'rgba(0,0,0,0)',
      'circle-stroke-color': '#3fbf85',
      'circle-stroke-width': 2.5,
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, byPop(5, 9), 8, byPop(9, 16)],
    },
  });
  addCityLabels();
}

// Mark (or clear) the open city's dot with the green selected ring, and exempt
// that city from the zoom/reading gates on the dot and name so the ring is never
// a bare unlabeled circle (see dotFilter/labelFilter). Passing null clears it.
export function setSelectedHighlight(id: number | null) {
  setHighlightId(id);
  if (!map || !map.getLayer('city-selected-ring')) return;
  const mine = isOpenCity();
  map.setFilter('city-selected-ring', mine);
  // Its own always-placed name, and its exemption from the shared dot/label gates.
  if (map.getLayer('city-selected-label')) map.setFilter('city-selected-label', mine);
  const v = state.variable;
  for (const lid of ['weather-glow', 'weather-points']) {
    if (map.getLayer(lid)) map.setFilter(lid, dotFilter(v));
  }
  if (map.getLayer('city-labels')) map.setFilter('city-labels', labelFilter(v));
}

// City names, above the dots.
//
// There is no zoom rule for how many labels fit — that depends on where the
// cities are and how long their names are, which only the renderer knows. So let
// MapLibre collide them out, and sort by population so that when two labels want
// the same space the bigger city keeps it. text-optional lets a crowded dot keep
// its dot and lose only its name.
export function addCityLabels() {
  map.addLayer({
    id: 'city-labels',
    type: 'symbol',
    source: 'weather',
    // Same variable test as the dots — a city with no reading draws no dot, and a name
    // hovering over nothing looks like a bug — but a zoom behind them, see namedAtZoom.
    // The open city is exempt from both (labelFilter), so its own page always names it.
    filter: labelFilter(state.variable),
    layout: {
      // The ASCII name, so a single vendored glyph range covers every label.
      'text-field': ['get', 'nameAscii'],
      'text-font': ['Noto Sans Regular'],
      // Size ramps on population *and* zoom, the way the dots do. At a world view the
      // spread stays narrow — everything on screen is a major city, and the names are
      // there to be found, not ranked. Zoomed in, the range opens up: a capital next to
      // a market town should look like one, and by then there is room to show it.
      'text-size': ['interpolate', ['linear'], ['zoom'], 1, byPop(10, 13), 8, byPop(11, 22)],
      'text-offset': [0, 0.9],
      'text-anchor': 'top',
      'text-padding': 4,
      'symbol-sort-key': ['-', 0, ['get', 'pop']], // biggest city wins a collision
      'text-allow-overlap': false,
      'text-optional': true,
    },
    paint: {
      'text-color': mapTheme().cityLabel,
      // The field underneath is any colour at all, so the halo is what keeps the
      // name readable rather than the text colour.
      'text-halo-color': mapTheme().labelHalo,
      'text-halo-width': 1.3,
    },
  });

  // The open city's name, always drawn. A twin of city-selected-ring: same
  // source, filtered to the one open city (set by setSelectedHighlight), but a
  // symbol layer with allow-overlap/ignore-placement so it is never gated by
  // zoom nor collided out by a bigger neighbour. This is what actually labels a
  // small city on its own /{country}/{city}/ page; the shared city-labels layer
  // above excludes the open city, so the name draws exactly once.
  map.addLayer({
    id: 'city-selected-label',
    type: 'symbol',
    source: 'weather',
    // isOpenCity() reads the current open-city id, so a deep-link that set it
    // before the layers were built is labelled from the first frame; -1 (nothing
    // open) matches no feature. setSelectedHighlight keeps it in step after.
    filter: isOpenCity(),
    layout: {
      'text-field': ['get', 'nameAscii'],
      'text-font': ['Noto Sans Regular'],
      'text-size': ['interpolate', ['linear'], ['zoom'], 1, byPop(10, 13), 8, byPop(11, 22)],
      'text-offset': [0, 0.9],
      'text-anchor': 'top',
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': mapTheme().cityLabel,
      'text-halo-color': mapTheme().labelHalo,
      'text-halo-width': 1.3,
    },
  });
}

// The places in a comparison, on the band above it: one dot and one name each,
// in the place's identity colour. MapLibre paints from literals, not from the
// stylesheet, so these are the --fc-cmp-* tokens mirrored by hand (the same
// convention CHART_THEME keeps) — dark theme first, the light theme's darker
// twins second.
export const CMP_HEX = {
  dark: ['#5ec386', '#e6964f', '#b897f0'],
  light: ['#249057', '#b16512', '#8766bb'],
};

export interface ComparePin {
  lon: number;
  lat: number;
  name: string;
  k: number;
}

export function setComparePins(pins: ComparePin[], light = false) {
  if (!map) return;
  const data: GeoJSON = {
    type: 'FeatureCollection',
    features: pins.map((p) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [p.lon, p.lat] }, properties: { name: p.name, k: p.k } })),
  };
  const hex = light ? CMP_HEX.light : CMP_HEX.dark;
  const color = ['match', ['get', 'k'], 0, hex[0], 1, hex[1], hex[2]] as unknown as string;
  const src = map.getSource('compare-pins') as GeoJSONSource | undefined;
  if (src) {
    src.setData(data);
  } else {
    map.addSource('compare-pins', { type: 'geojson', data });
    map.addLayer({
      id: 'compare-pin-dots',
      type: 'circle',
      source: 'compare-pins',
      paint: { 'circle-radius': 7, 'circle-color': color, 'circle-stroke-width': 2, 'circle-stroke-color': light ? '#fbfaf7' : '#070a0d' },
    });
    map.addLayer({
      id: 'compare-pin-labels',
      type: 'symbol',
      source: 'compare-pins',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 15,
        'text-offset': [0, 1.1],
        'text-anchor': 'top',
        'text-allow-overlap': true,
      },
      paint: { 'text-color': color, 'text-halo-color': light ? 'rgba(251,250,247,0.9)' : 'rgba(4,7,10,0.9)', 'text-halo-width': 1.4 },
    });
  }
  map.setPaintProperty('compare-pin-dots', 'circle-color', color);
  map.setPaintProperty('compare-pin-labels', 'text-color', color);
  const vis = pins.length ? 'visible' : 'none';
  map.setLayoutProperty('compare-pin-dots', 'visibility', vis);
  map.setLayoutProperty('compare-pin-labels', 'visibility', vis);
}
